import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { DPE_STEPS } from '../../../../../@types/DPE';
import type { Owner } from '../../../../../@types/Owner';
import {
  initOwners, setCurrentStep, setLastCompletionType, setNbOwner, setOwner,
} from '../../../../../store/DpeReducer';
import { useAppDispatch, useAppSelector } from '../../../../../store/hook';
import type { RootState } from '../../../../../store/store';
import { JWTAuthorization } from '../../../../../utils/JWTHeader';
import { isValidEmail, isValidFrenchPhone } from '../../../../../utils/validation';
import Popup from '../../../../common/Popup/Popup.module';
import Spinner from '../../../../common/Spinner';
import { DPE_QUALIF_STEPS } from '../dpeQualifStep';
import DpeOwnerForm from './DpeOwnerForm.module';

function DpeStep2() {
  const dispatch = useAppDispatch();
  const jwt = useSelector((state: RootState) => state.auth.token);
  const currentStep = useAppSelector((state) => state.dpe.currentStep);
  const owners = useAppSelector((state) => state.dpe.owners);
  const selectedDpe = useAppSelector((state) => state.dpe.selectedDpe);
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<number, { lastname?: string; phone?: string; email?: string }>>({});
  const [showConfirmPopup, setShowConfirmPopup] = useState<boolean>(false);
  const [canAddOwner, setCanAddOwner] = useState<boolean>(true);

  useEffect(() => {
    if (owners.length === 0) {
      dispatch(initOwners());
    }
  }, []);

  const validateOwners = () => {
    let isValid = true;
    const newErrors: Record<number, { lastname?: string; phone?: string; email?: string }> = {};

    owners.forEach((owner, index) => {
      const ownerErrors: { lastname?: string; phone?: string; email?: string } = {};

      if (!owner.lastname) {
        ownerErrors.lastname = 'Le nom est obligatoire';
        isValid = false;
      }

      if (owner.phone && !isValidFrenchPhone(owner.phone)) {
        ownerErrors.phone = 'Numéro de téléphone invalide';
        isValid = false;
      }

      if (owner.email && !isValidEmail(owner.email)) {
        ownerErrors.email = 'Adresse email invalide';
        isValid = false;
      }

      setErrors(ownerErrors);

      if (Object.keys(ownerErrors).length > 0) {
        newErrors[index] = ownerErrors;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const proceedToNextStep = () => {
    setLoading(true);
    const body = owners.map((owner) => ({
      firstname: owner.firstname,
      lastname: owner.lastname,
      postalCode: owner.postalCode,
      city: owner.city,
      address: owner.address,
      livingIn: owner.livingIn,
      email: owner.email,
      phone: owner.phone,
      civility: owner.civility,
      floorNumber: owner.floorNumber,
      doorNumber: owner.doorNumber,
      lotNumber: owner.lotNumber,
      surface: owner.surface,
    }));
    fetch(`/api/dpes/${selectedDpe.id}/owners/batch`, {
      method: 'POST',
      headers: {
        Authorization: JWTAuthorization(jwt),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    }).then((_response) => _response.json())
      .then(() => {
        fetch(`/api/dpes/${selectedDpe.id}/treat`, {
          method: 'PATCH',
          headers: {
            Authorization: JWTAuthorization(jwt),
            'Content-Type': 'application/json',
          },
        }).then((_response) => _response.json())
          .then(() => {
            fetch(`/api/dpes/${selectedDpe.id}`, {
              method: 'PATCH',
              headers: {
                Authorization: JWTAuthorization(jwt),
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ step: DPE_STEPS.IDENTIFIED }),
            }).then((_response) => _response.json())
              .then(() => {
                dispatch(setLastCompletionType('identified'));
                dispatch(setCurrentStep(DPE_QUALIF_STEPS.REDIRECTION_TO_DPE_OR_IDENTIFIED));
              }).catch((error) => {
                console.error('Error next step dpe :', error);
              });
          }).catch((error) => {
            console.error('Error treating dpe :', error);
          });
      }).catch((error) => {
        console.error('Error adding owner :', error);
      });
  };

  const handleConfirmOwners = () => {
    setShowConfirmPopup(false);
    setCanAddOwner(false);
    proceedToNextStep();
  };

  const handleNextStep = () => {
    if (!validateOwners()) {
      console.error('Validation failed, errors :', errors);
      return;
    }
    
    setShowConfirmPopup(true);
  };

  const handleAddOwner = () => {
    if (!canAddOwner) {return;}
    
    dispatch(setOwner([
      {
        firstname: '',
        lastname: '',
        postalCode: '',
        city: '',
        address: '',
        livingIn: true,
        email: '',
        phone: '',
        civility: 1,
        floorNumber: '',
        doorNumber: '',
        lotNumber: '',
        surface: 0,
        contacts: [],
        interaction: null,
      } as Owner,
      owners.length]));
    dispatch(setNbOwner(owners.length + 1));
  };

  return (
    <div>
      <div className="flex flex-wrap justify-center md:justify-evenly">
        {owners.map((owner, ownerIndex) => (
          <div
            key={ownerIndex}
            className="mb-5 w-full rounded p-1 shadow md:w-1/2 lg:w-1/2"
          >
            <DpeOwnerForm key={ownerIndex} ownerIndex={ownerIndex} owner={owner} formErrors={errors[ownerIndex]} />
          </div>
        ))}
      </div>
      <div className="mt-5 flex justify-between">
        <button
          type="button"
          className={`w-auto rounded-xl ${canAddOwner ? 'bg-rgpd-blue-dark' : 'bg-gray-400'} m-auto px-5 py-3 text-center text-lg font-semibold text-white shadow-sm`}
          onClick={handleAddOwner}
          disabled={!canAddOwner}
        >
          Ajouter un propriétaire
        </button>
        {currentStep === DPE_QUALIF_STEPS.COMPLETING_OWNERS_DETAILS
            && (
              <button
                type="button"
                className="m-auto w-auto rounded-xl bg-rgpd-green px-5 py-3 text-center text-lg font-semibold text-white shadow-sm"
                onClick={handleNextStep}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner aria-label="Center-aligned spinner" size="lg" className="mr-1" />
                    Chargement...
                  </>
                ) : 'Passer à l\'étape suivante' }
              </button>
            )}
      </div>

      <Popup
        isOpen={showConfirmPopup}
        onClose={() => setShowConfirmPopup(false)}
        title="Confirmation du nombre de propriétaires"
        width="500px"
      >
        <div className="flex flex-col">
          <p>
            Vous avez renseigné <strong>{owners.length}</strong> propriétaire{owners.length > 1 ? 's' : ''}.
          </p>
          <p className="font-semibold text-rgpd-red">
            Une fois confirmé, vous ne pourrez plus ajouter de propriétaires supplémentaires.
          </p>
          <p className="my-6">
            Êtes-vous sûr que c'est le nombre correct de propriétaires potentiels ?
          </p>
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              className="rounded-lg bg-gray-200 px-4 py-2 text-gray-800 hover:bg-gray-300"
              onClick={() => setShowConfirmPopup(false)}
            >
              Annuler
            </button>
            <button
              type="button"
              className="rounded-lg bg-rgpd-green px-4 py-2 text-white hover:bg-green-600"
              onClick={handleConfirmOwners}
            >
              Confirmer
            </button>
          </div>
        </div>
      </Popup>
    </div>
  );
}

export default DpeStep2;
